import React, {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Loader2, Paperclip, Send } from 'lucide-react';

import { Textarea } from 'components/ui/textarea';
import { Button } from 'components/ui/button';
import { useCreateComment } from 'services/help-desk/hooks';
import { getDateTime } from 'lib/utils';
import { Input } from 'components/ui/input';
import { HelpDeskTicketDetail } from 'services/help-desk/type';
import Loader from 'components/Loader';
import { ScrollArea } from 'components/ui/scroll-area';

interface CommentsProps {
  ticketDetails: HelpDeskTicketDetail;
  ticketId: string;
}
function Comments({ ticketDetails, ticketId }: CommentsProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const fileInputRef = useRef(null);

  const [newMessage, setNewMessage] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [userFileURLs, setUserFileURLs] = useState<Record<string, string>>();
  const [isNewMessagesShown, setIsNewMessagesShown] = useState<number | null>(
    null
  );

  const { mutate: createComment, isPending: isLoading } = useCreateComment({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['help-desk-details', ticketId],
      });
    },
  });

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
  };

  const hanldeUpdateTicket = () => {
    if (ticketDetails) {
      const params = {
        event_type: 'communication',
        ticket_id: ticketDetails?.ticket_detail[0]?.ticket_id,
      };
      const formData = new FormData();
      formData.append('message', newMessage);
      selectedFiles.forEach((file, index) => {
        formData.append(`file_${index + 1}`, file);
      });
      createComment({ params, data: formData });
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...Array.from(files)]);
    }
  };
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (fileInputRef.current) {
      (fileInputRef.current as HTMLInputElement).click();
    }
  };

  useEffect(() => {
    if (ticketDetails && isNewMessagesShown) {
      const params = {
        ticket_id: ticketDetails?.ticket_detail[0]?.ticket_id,
      };
      const formData = new FormData();
      formData.append('read_flag', 'true');
      createComment({ params, data: formData });
    }
  }, []);

  useEffect(() => {
    const filesToRender = ticketDetails.attachment_detail;
    const attachmentMap: Record<string, string> = filesToRender.reduce(
      (map, file) => {
        map[file.attachment_id] = file.attachment_link;
        return map;
      },
      {} as Record<string, string>
    );
    setUserFileURLs(attachmentMap);
    if (ticketDetails) {
      const falseIndex = ticketDetails?.communication_detail.findIndex(
        (comment) => !comment.read_flag
      );
      setIsNewMessagesShown(falseIndex);
    }
  }, [ticketDetails]);

  return (
    <div>
      <div className='col-span-1 '>
        <div className='flex items-center justify-between py-2'>
          <p className='text-base md:text-xl'>{t('comments')}</p>

          <p className='flex items-center justify-end text-sm'>
            <span className='text-sm lg:text-base'>{t('ticket_id')}:</span>{' '}
            <span className='hidden lg:block'>
              {ticketDetails?.ticket_detail?.[0]?.ticket_id}
            </span>
            <span className='block lg:hidden'>
              {ticketDetails?.ticket_detail?.[0]?.ticket_id.split('-').pop()}
            </span>
          </p>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <div className='px-4 py-4 space-y-2 border-2 border-primary/40 rounded-xl'>
            {ticketDetails?.communication_detail?.length ? (
              <ScrollArea className='h-[calc(100vh-24.75rem)] p-4 space-y-2 px-4'>
                {ticketDetails?.communication_detail.map((comment, index) => {
                  const { date, time } = getDateTime(
                    comment?.lastmodified_at,
                    'dd/MM/yyyy'
                  );

                  if (comment.sent_by === 'user') {
                    return (
                      <div
                        key={index}
                        className='flex justify-end ml-32 text-sm '
                      >
                        <div className='flex flex-col'>
                          <p className='font-semibold text-end'>
                            {comment.sent_by === 'user' && t('you')}:
                          </p>
                          <div className='w-full px-4 py-2 rounded-md bg-primary/20'>
                            <p>{comment.message}</p>
                            <div className='flex items-center justify-between mt-2'>
                              {JSON.parse(comment?.attachment_id)?.length ? (
                                <div className='flex gap-1'>
                                  {JSON.parse(comment?.attachment_id)?.map(
                                    (item: string, index: number) => {
                                      return (
                                        <a
                                          className='flex flex-wrap'
                                          href={userFileURLs?.[item]}
                                          target='_blank'
                                          key={index}
                                        >
                                          <Paperclip className='w-3 h-3' />
                                        </a>
                                      );
                                    }
                                  )}
                                </div>
                              ) : null}

                              <p className='flex flex-wrap items-center justify-end gap-2 ml-4 text-xs text-end '>
                                <span>{date}</span>
                                <span>{time}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <React.Fragment key={index}>
                        {isNewMessagesShown === index && (
                          <div className='grid items-center w-full grid-cols-6'>
                            <p className='h-0 col-span-2 border-2 border-primary/20'></p>
                            <p className='col-span-2 px-2 text-center'>
                              New Messages
                            </p>
                            <p className='h-0 col-span-2 border-2 border-primary/20'></p>
                          </div>
                        )}
                        <div className='flex justify-start mr-32 text-sm'>
                          <div className='flex flex-col'>
                            <p className='font-semibold'>
                              {comment.sent_by === 'agent' && 'Agent'}:
                            </p>
                            <div className='w-full px-4 py-2 rounded-md bg-primary/10'>
                              <p>{comment.message}</p>
                              <p className='flex flex-wrap items-center justify-end gap-2 mt-2 text-xs text-end'>
                                <span>{date}</span>
                                <span>{time}</span>
                                {comment.read_flag && (
                                  <span>
                                    <CheckCheck className='w-4 h-4 font-semibold text-green-600' />
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  }
                })}
              </ScrollArea>
            ) : (
              <p>{t('no_comments')}</p>
            )}

            {ticketDetails?.ticket_detail[0].status === 'closed' ? null : (
              <div className='flex justify-between gap-2 px-2 mt-4 '>
                <div className='relative flex flex-col w-full border-2 rounded-md border-primary/40'>
                  <Textarea
                    className='h-6 border-none sm:h-28 focus-visible:ring-0 focus-visible:ring-offset-0 '
                    placeholder={t('write_ticket_message')}
                    onChange={handleInputChange}
                    value={newMessage}
                  />
                  {selectedFiles.length ? (
                    <div className='flex flex-wrap w-3/4 gap-1 m-1'>
                      {selectedFiles.map((item, index) => (
                        <div
                          key={item.name + index}
                          className='px-1 border rounded-md border-primary/40 bg-primary/20'
                        >
                          {item.name}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div className='absolute bottom-0 right-0 flex items-center justify-end mb-2 mr-2'>
                    <button
                      className='flex items-center justify-end p-1'
                      onClick={handleClick}
                    >
                      <Paperclip className='w-5 h-5 text-primary/80' />
                      <span className='sr-only'>Upload attachment</span>
                    </button>
                    <Input
                      ref={fileInputRef}
                      type='file'
                      multiple
                      className='hidden border-none'
                      onChange={handleFileChange}
                      data-testid='upload-attachment'
                    />
                    <Button
                      disabled={!newMessage}
                      className='flex items-center justify-center gap-2 px-6 bg-transparent md:bg-primary/70 text-primary md:text-white'
                      onClick={() => hanldeUpdateTicket()}
                    >
                      {isLoading && (
                        <Loader2 className='w-4 h-4 animate-spin' />
                      )}
                      <Send className='w-4 h-4' />
                      <span className='hidden md:block'>{t('send')}</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Comments;
